import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { m365Api } from '../services/api';
import { M365Status } from '../types';
import './Microsoft365.css';

const REQUIRED_SCOPES = [
  'offline_access',
  'openid',
  'profile',
  'email',
  'User.Read.All',
  'Group.Read.All',
  'Reports.Read.All',
  'Directory.Read.All',
];

function Microsoft365() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<M365Status | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ client_id: '', tenant_id: '', client_secret: '' });

  // Check if user is admin or superadmin
  const isAdmin = user && (user.role === 'admin' || user.role === 'superadmin' || user.is_superuser);

  // Redirect non-admin users
  useEffect(() => {
    if (!authLoading && !isAdmin) {
      toast.error('Access denied. Admin privileges required.');
      navigate('/');
    }
  }, [authLoading, isAdmin, navigate]);

  const loadStatus = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const result = await m365Api.getStatus();
      setStatus(result);
      setForm(prev => ({
        ...prev,
        client_id: result.client_id ?? '',
        tenant_id: result.tenant_id ?? '',
        client_secret: '',
      }));
      setError(null);
    } catch (err: any) {
      console.error(err);
      setError(err?.response?.data?.detail || err.message || 'Failed to load Microsoft 365 status');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      loadStatus();
    }
  }, [isAdmin, loadStatus]);

  const formatDate = (value?: string | null) => {
    if (!value) return '-';
    const date = new Date(value);
    return isNaN(date.getTime()) ? '-' : date.toLocaleString();
  };

  const handleInput = (field: 'client_id' | 'tenant_id' | 'client_secret') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm(prev => ({ ...prev, [field]: e.target.value }));
    };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    const password = window.prompt('Please enter your password to save Microsoft 365 credentials:');
    if (!password) {
      toast.error('Password required to save Microsoft 365 settings');
      return;
    }

    setSaving(true);
    try {
      const payload: { client_id: string; tenant_id: string; client_secret?: string; password: string } = {
        client_id: form.client_id.trim(),
        tenant_id: form.tenant_id.trim(),
        password,
      };
      if (form.client_secret.trim()) {
        payload.client_secret = form.client_secret.trim();
      }
      const result = await m365Api.saveSettings(payload);
      setStatus(result);
      setForm(prev => ({ ...prev, client_secret: '' }));
      toast.success('Microsoft 365 settings saved');
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.detail || err.message || 'Failed to save Microsoft 365 settings');
    } finally {
      setSaving(false);
    }
  };

  const handleConnect = async () => {
    try {
      const response = await m365Api.connect(false);
      const url = response.redirect_url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
        toast('Complete the Microsoft 365 consent window to finish connecting.');
      } else {
        toast.error('Authorization URL was not returned');
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || 'Failed to start Microsoft 365 consent flow');
    }
  };

  const handleDisconnect = async () => {
    const password = window.prompt('Please enter your password to confirm disconnecting Microsoft 365:');
    if (!password) {
      toast.error('Password required to disconnect Microsoft 365');
      return;
    }

    try {
      await m365Api.disconnect(password);
      toast.success('Microsoft 365 disconnected');
      await loadStatus();
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.detail || err.message || 'Failed to disconnect Microsoft 365');
    }
  };

  const handleRefresh = async () => {
    try {
      const result = await m365Api.refreshTokens();
      setStatus(result);
      toast.success('Microsoft 365 tokens refreshed');
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || 'Failed to refresh Microsoft 365 tokens');
    }
  };

  const handleImport = async () => {
    if (!status?.connected) {
      toast.error('Connect Microsoft 365 first');
      return;
    }
    const now = new Date();
    const periodMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    try {
      const result = await m365Api.importUsage(periodMonth);
      toast.success(result.message || 'Microsoft 365 import started');
    } catch (err: any) {
      console.error(err);
      toast.error(err?.response?.data?.detail || err.message || 'Failed to start Microsoft 365 import');
    }
  };

  // Show loading while checking auth
  if (authLoading) {
    return <div className="m365-page"><div className="loading">Loading...</div></div>;
  }

  // Don't render if not admin (redirect will happen)
  if (!isAdmin) {
    return null;
  }

  return (
    <div className="m365-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Microsoft 365 Integration</h1>
          <p className="page-description">Configure Azure AD credentials and manage the Microsoft 365 consent flow.</p>
        </div>
        {status && (
          <span className={`status-pill ${status.connected ? 'status-pill--success' : 'status-pill--muted'}`}>
            {status.connection_status || 'unknown'}
          </span>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div className="loading">Loading Microsoft 365 status...</div>
      ) : (
        <>
          {/* Connection Status Card */}
          <div className="card">
            <h2>Connection Status</h2>
            <div className="status-grid">
              <div className="status-item">
                <span className={`status-dot ${status?.connected ? 'connected' : 'disconnected'}`} />
                <div>
                  <div className="status-title">Tenant Connection</div>
                  <div className="status-detail">{status?.connected ? 'Connected' : 'Not connected'}</div>
                </div>
              </div>
              <div className="status-item simple">
                <div className="status-title">Client ID</div>
                <div className="status-detail monospace">{status?.client_id || 'Not configured'}</div>
              </div>
              <div className="status-item simple">
                <div className="status-title">Tenant ID</div>
                <div className="status-detail monospace">{status?.tenant_id || 'Not configured'}</div>
              </div>
              <div className="status-item simple">
                <div className="status-title">Client Secret</div>
                <div className="status-detail">{status?.has_client_secret ? 'Stored securely' : 'Not configured'}</div>
              </div>
              <div className="status-item simple">
                <div className="status-title">Last Connected</div>
                <div className="status-detail">{formatDate(status?.connected_at)}</div>
              </div>
            </div>
          </div>

          {/* Info Cards Grid */}
          <div className="info-grid">
            <div className="card info-card">
              <h3>Required Microsoft Graph Permissions</h3>
              <p className="card-subtitle">Add these delegated permissions to your Azure app registration and grant admin consent.</p>
              <div className="scope-chips">
                {REQUIRED_SCOPES.map(scope => (
                  <span key={scope} className="chip">{scope}</span>
                ))}
              </div>
            </div>

            <div className="card info-card">
              <h3>Quick Setup Guide</h3>
              <ol className="steps-list">
                <li>In Azure Portal, create a multitenant app registration.</li>
                <li>Add redirect URI: <code>https://cloudview.cloudits.com/api/v1/m365/callback</code></li>
                <li>Add the permissions above, then grant admin consent.</li>
                <li>Generate a client secret and copy credentials below.</li>
                <li>Start consent and complete the Microsoft sign-in.</li>
              </ol>
              <p className="note">Tip: Partner-delegated admins should use the customer tenant ID when initiating consent.</p>
            </div>
          </div>

          {/* Settings Form */}
          <div className="card">
            <h2>Azure AD Credentials</h2>
            <form className="m365-form" onSubmit={handleSave}>
              <div className="form-row">
                <label>Microsoft Application (Client) ID</label>
                <input
                  value={form.client_id}
                  onChange={handleInput('client_id')}
                  placeholder="00000000-0000-0000-0000-000000000000"
                  required
                />
              </div>

              <div className="form-row">
                <label>Microsoft Directory (Tenant) ID</label>
                <input
                  value={form.tenant_id}
                  onChange={handleInput('tenant_id')}
                  placeholder="00000000-0000-0000-0000-000000000000"
                  required
                />
              </div>

              <div className="form-row">
                <label>Client Secret {status?.has_client_secret && '(leave blank to keep current)'}</label>
                <input
                  type="password"
                  value={form.client_secret}
                  onChange={handleInput('client_secret')}
                  placeholder={status?.has_client_secret ? '••••••••••••••••' : 'Enter client secret'}
                />
              </div>

              <div className="form-actions">
                <button className="btn btn-primary" type="submit" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Settings'}
                </button>
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={handleConnect}
                  disabled={!status?.client_id || !status?.tenant_id}
                >
                  Start Consent
                </button>
                <button
                  className="btn btn-outline"
                  type="button"
                  onClick={handleRefresh}
                  disabled={!status?.connected}
                >
                  Refresh Tokens
                </button>
                <button
                  className="btn btn-outline"
                  type="button"
                  onClick={handleImport}
                  disabled={!status?.connected}
                >
                  Sync Current Month
                </button>
                <button
                  className="btn btn-danger"
                  type="button"
                  onClick={handleDisconnect}
                  disabled={!status?.connected}
                >
                  Disconnect
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}

export default Microsoft365;
